// apps/web/src/context/AuthContext.js

import React, { createContext, useEffect, useState } from 'react';
import { sentiaAuthService, tasksService } from '../services/api';

/**
 * AuthContext is the single source of truth for:
 * - Are we authenticated?
 * - Who is the current user (from Sentia Tasks DB)?
 * - Do we currently have a valid Sentia JWT in localStorage?
 *
 * Flow:
 *   1. User logs in against Sentia (not the Tasks API).
 *      -> We get a JWT ("token") and a Sentia user object.
 *      -> We store that JWT in localStorage under "token".
 *
 *   2. After storing the token, we call tasksService.getMe().
 *      -> Tasks API verifies the token, upserts the user into its own DB,
 *         and returns a canonical user object.
 *
 *   3. We store that canonical Tasks user in state as `user`.
 *
 * On page reload:
 *   - We read localStorage.getItem('accessToken').
 *   - If it exists, we call tasksService.getMe() immediately and repopulate.
 *
 * Logout:
 *   - We remove the token from localStorage.
 *   - We clear local state.
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // { id, email, role, subscription_tier, created_at }
  const [limits, setLimits] = useState(null); // { workspaces: { current, max }, lists_per_workspace }
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // block UI until we know

  // Helper: hydrate the user from Tasks API using existing token
  const hydrateFromTasksAPI = async () => {
    try {
      const { data } = await tasksService.getMe();
      if (data && data.user) {
        setUser(data.user);
        setLimits(data.limits || null); // Store limits from API response
        setIsAuthenticated(true);
      } else {
        // If Tasks API didn't return a user, treat as logged out
        console.warn('[AuthContext] /auth/me returned no user.');
        setUser(null);
        setLimits(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.warn('[AuthContext] Failed to hydrate from /auth/me:', err);
      // If /auth/me fails (e.g. bad token), clear auth
      localStorage.removeItem('accessToken');
      setUser(null);
      setLimits(null);
      setIsAuthenticated(false);
    }
  };

  // On mount, if we already have a Sentia token in localStorage, try to hydrate.
  // Also check for ?auth= URL parameter (SSO from Sentia platform)
  useEffect(() => {
    (async () => {
      // Check if JWT is passed via URL parameter (from Sentia SSO)
      const urlParams = new URLSearchParams(window.location.search);
      const authToken = urlParams.get('auth');

      if (authToken) {
        // URLSearchParams automatically decodes the token
        // Store the token from URL parameter
        localStorage.setItem('accessToken', authToken);

        // Clean up URL (remove auth parameter for security)
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);

        // Hydrate user from Tasks API
        await hydrateFromTasksAPI();
      } else {
        // Normal flow: check localStorage for existing token
        const token = localStorage.getItem('accessToken');
        if (token) {
          await hydrateFromTasksAPI();
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * login(email, password)
   *
   * Calls Sentia's auth endpoint to get a token.
   * Stores that token in localStorage.
   * Then hydrates via the Tasks API to get the canonical user.
   */
  const login = async (email, password) => {
    try {
      const { data } = await sentiaAuthService.login(email, password);

      if (!data || !data.token) {
        throw new Error('Sentia login did not return a token');
      }

      // Save the Sentia JWT locally
      localStorage.setItem('accessToken', data.token);

      // Now hydrate from Tasks API (this upserts into the Tasks DB and returns canonical user)
      await hydrateFromTasksAPI();

      return { ok: true };
    } catch (error) {
      console.error('[AuthContext] Login failed:', error);
      // Clean up partial state
      localStorage.removeItem('accessToken');
      setUser(null);
      setIsAuthenticated(false);
      return {
        ok: false,
        message:
          error?.response?.data?.message ||
          error.message ||
          'Login failed for unknown reason.',
      };
    }
  };

  /**
   * logout()
   *
   * We do not call any API here, because Tasks API no longer
   * "owns" sessions. Sentia is the IdP, and this frontend
   * just drops the token to "log out".
   */
  const logout = () => {
    localStorage.removeItem('accessToken');
    setUser(null);
    setLimits(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    limits,  // Expose limits to consumers
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshProfile: hydrateFromTasksAPI, // manual "Get Me" button can call this
  };

  if (isLoading) {
    // Simple gate. You can replace this with a spinner component later.
    return <div>Loading...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Convenience hook
export const useAuth = () => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};
